/**
 * Resource + MEMORY dependency graph adapter (plans/resource-collection.md).
 *
 * Turns the weak resource registry into a {@link NodeGraph} with
 * `domain: "resources"` — an ego-graph rooted at one resource, wired by the
 * usage edges the tracker already records, weighted by VRAM. It answers the
 * top forum pain: "what's eating memory, who keeps it alive, what's dangling?"
 *
 * PURE and three-free: it reads only `IResourceService` (entries + getUsages)
 * and an injected `memoryFor(resource) → {cpu, vram}` (the caller passes the
 * real estimators; tests pass a fake). Weak throughout — nodes carry entry ids
 * + a transient `deref`, never a strong reference (the host-lifetime rule).
 *
 * Edge direction is producer → consumer (RESOURCE → its user), matching the
 * layout's `from→to = left→right` rule and the TSL shader graph (inputs on the
 * left). An ego-graph rooted at a material reads left→right: textures → material
 * → meshes; a model root reads textures → materials/geometry → meshes → model —
 * raw assets on the left assembling into bigger things on the right.
 */

import { createGraph, type GraphNode, type NodeGraph } from "../../nodegraph/model.js";
import { resourceUsageUuid, type IResourceEntry, type IResourceService, type ResourceUsage } from "./resources.js";

/** GPU (`vram`) + CPU/heap (`cpu`) byte footprint of a resource. */
export interface ResourceMemory { cpu: number; vram: number }

/** Live state of ONE AnimationAction on a mixer (a clip + how it is playing). */
export interface AnimationActionInfo {
    /** the action's clip name — the display handle */
    clipName: string;
    /** clip length in seconds (0 when unknown) */
    duration: number;
    /** playhead within the clip, seconds */
    time: number;
    /** EFFECTIVE weight (0..1) — what actually contributes to the blend */
    weight: number;
    timeScale: number;
    running: boolean;
    paused: boolean;
    /** "LoopRepeat" | "LoopOnce" | "LoopPingPong" */
    loop?: string;
}

/** Live animation state of an AnimationMixer — its actions and clock. */
export interface MixerAnimationInfo {
    /** mixer clock, seconds */
    time: number;
    timeScale: number;
    actions: AnimationActionInfo[];
}

export interface ResourceGraphOptions {
    /** GPU + CPU bytes for a live resource — injected so the adapter stays
     *  three-free. Undefined (or both 0) for things without a footprint
     *  (meshes, scenes, materials). */
    memoryFor?: (resource: object) => ResourceMemory | undefined;
    /** Deduped GPU+CPU bytes across a SET of resources — storage shared between
     *  them (an ArrayBuffer used by two geometries, an image by two textures)
     *  counts once. Injected (three-free) for the root rollup total. Falls back to
     *  a per-node sum when absent. */
    dedupedMemoryFor?: (resources: object[]) => ResourceMemory;
    /**
     * A lazy thumbnail for a resource (textures) — injected so the adapter stays
     * three-free and preview-service-free. `produce` resolves to an image URL
     * (or null); `aspect` sizes the card before it loads. Undefined = no preview.
     */
    previewFor?: (resource: object) => { produce: () => Promise<string | null>; aspect?: number } | undefined;
    /** A short format label for a resource (a texture's GPU compression: "BC7",
     *  "ASTC 6x6", "uncompressed (RGBA)", …) — injected, so it shows on the node
     *  without opening the asset. Undefined for resources with no such label. */
    formatFor?: (resource: object) => string | undefined;
    /** A dimensions label for a resource (a texture's "1280×1280") — injected. */
    sizeFor?: (resource: object) => string | undefined;
    /** A mip-level count for a resource (a texture's "9", or "9 (generated)") —
     *  injected. Undefined when the resource has no mip concept or it is unknown. */
    mipsFor?: (resource: object) => string | undefined;
    /**
     * Live animation state for an AnimationMixer — injected (three-free adapter).
     * When it returns info for the ROOT, the graph becomes clips → mixer instead of
     * the usage/memory walk: a mixer has no registry usage edges, so it would
     * otherwise render as a lone node. Undefined for anything that isn't a mixer.
     * Values are a SNAPSHOT; the graph service re-reads them to animate weights.
     */
    animationFor?: (resource: object) => MixerAnimationInfo | undefined;
    /** Hops out from the root. A model reaches its textures via
     *  model → mesh → material → texture (3 hops), so default 3. */
    hops?: number;
    /** Hard node cap; excess collapses into one "N more…" node. Default 60. */
    maxNodes?: number;
    /**
     * Pro gate: keep the first `redactAfter` nodes (root + nearest, BFS order)
     * fully detailed; the rest still RENDER — same nodes and edges, so the
     * structure and totals are visible — but with their identity/memory
     * stripped and `meta.redacted = true` (a locked tease). `null`/undefined =
     * no redaction (Pro).
     */
    redactAfter?: number | null;
}

const num = (n: number): string => (n >= 10 || Number.isInteger(n) ? String(Math.round(n)) : n.toFixed(1));
const bytesLabel = (bytes: number): string => {
    if (bytes <= 0) return "";
    const mb = bytes / (1024 * 1024);
    if (mb >= 1) return `${num(mb)} MB`;
    return `${num(bytes / 1024)} KB`;
};

const clamp01 = (n: number): number => (Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : 0);
/** "72%" — the blend contribution, the number people actually compare. */
const weightLabel = (weight: number): string => `${Math.round(clamp01(weight) * 100)}%`;
const secondsLabel = (s: number): string => (Number.isFinite(s) ? `${s.toFixed(2)}s` : "—");

/** Per-action node meta. `weight`/`progress` are 0..1 NUMBERS so the card can draw
 *  bars; the *Label fields are the text beside them. `animation: true` tells the
 *  card this is live state, not a memory node (no CPU/GPU rows apply). */
function actionMeta(action: AnimationActionInfo): Record<string, unknown> {
    const weight = clamp01(action.weight);
    return {
        animation: true,
        value: action.running ? "playing" : "idle",
        weight,
        weightLabel: weightLabel(weight),
        progress: action.duration > 0 ? clamp01(action.time / action.duration) : 0,
        timeLabel: `${secondsLabel(action.time)} / ${secondsLabel(action.duration)}`,
        running: action.running,
        ...(action.paused ? { paused: true } : {}),
        // 1× is the default and just adds noise; show it only when retimed
        ...(action.timeScale !== 1 ? { timeScaleLabel: `${action.timeScale}×` } : {}),
        ...(action.loop ? { loop: action.loop } : {}),
        // an action at zero weight contributes NOTHING to the pose — same
        // "present but doing nothing" idea the registry calls dangling
        ...(action.running && weight <= 0 ? { dangling: true } : {}),
    };
}

/** Root (mixer) node meta: its clock and how many actions are actually blending. */
function applyMixerMeta(node: GraphNode, info: MixerAnimationInfo): void {
    const active = info.actions.filter((a) => a.running && a.weight > 0).length;
    Object.assign(node.meta ??= {}, {
        animation: true,
        value: "AnimationMixer",
        timeLabel: secondsLabel(info.time),
        actionCount: info.actions.length,
        activeCount: active,
        ...(info.timeScale !== 1 ? { timeScaleLabel: `${info.timeScale}×` } : {}),
    });
}

/** Build the resource dependency ego-graph rooted at `root`. */
export function buildResourceGraph(root: IResourceEntry, service: IResourceService, options: ResourceGraphOptions = {}): NodeGraph {
    const hops = options.hops ?? 3;
    const maxNodes = options.maxNodes ?? 60;
    const memoryFor = options.memoryFor;

    const graph = createGraph(`resources-${root.id}`, "resources", root.name || root.type, root.type);

    // Index everything ONCE: uuid → entry, and the forward "uses" edges
    // (consumer uuid → the entries it consumes) inverted from getUsages
    // (which is the reverse index: resource → its consumers).
    const entries = [...service.entries()];
    // Key by the usage-uuid (three uuid, or kind#id for uuid-less model wrappers)
    // so model→resource edges resolve — the tracker records them under the same key.
    const byUuid = new Map<string, IResourceEntry>();
    for (const e of entries) byUuid.set(resourceUsageUuid(e), e);
    const usesIndex = new Map<string, IResourceEntry[]>(); // consumerUuid → entries it uses
    for (const e of entries) {
        // A model's "consumers" are its CONTENTS (model → mesh), not "the mesh
        // uses the model" — leaving models in here draws a spurious reverse
        // mesh → model edge when the mesh expands. Containment ≠ usage.
        if (e.kind === "model") continue;
        for (const consumer of service.getUsages(e)) {
            let list = usesIndex.get(consumer.uuid);
            if (!list) usesIndex.set(consumer.uuid, list = []);
            list.push(e);
        }
    }
    // #region node creation (deduped)

    const nodeIds = new Map<string, string>(); // entry-id / consumer-uuid → graph node id
    const nodeById = new Map<string, GraphNode>();
    const ZERO_MEM: ResourceMemory = { cpu: 0, vram: 0 };
    const memOf = (entry: IResourceEntry): ResourceMemory => {
        if (!memoryFor) return ZERO_MEM;
        const live = entry.deref();
        return (live ? memoryFor(live) : undefined) ?? ZERO_MEM;
    };

    const entryNode = (entry: IResourceEntry): string => {
        const key = `e${entry.id}`;
        const existing = nodeIds.get(key);
        if (existing) return existing;
        const mem = memOf(entry);
        // dangling = alive with zero scene consumers AFTER the first usage pass
        const dangling = service.usagesReady && entry.alive && service.getUsages(entry).length === 0;
        // Deref once for the injected, three-aware bits (thumbnail + format).
        const live = (options.previewFor || options.formatFor || options.sizeFor || options.mipsFor) && entry.alive ? entry.deref() : undefined;
        const preview = live && options.previewFor ? options.previewFor(live) : undefined;
        // A texture's format ("BC7", "uncompressed", …) + dimensions ("1280×1280")
        // shown as their own rows, so they read at a glance without opening the asset.
        const format = live && options.formatFor ? options.formatFor(live) : undefined;
        const size = live && options.sizeFor ? options.sizeFor(live) : undefined;
        const mips = live && options.mipsFor ? options.mipsFor(live) : undefined;
        const node: GraphNode = {
            id: key,
            typeRef: `resource/${entry.kind}`,
            title: entry.name || entry.type,
            category: entry.kind,
            // inputs are added lazily as edges connect (named by binding slot,
            // or a fallback "uses") — see addEdge
            inputs: [],
            // producer side: this resource is "used by" whatever it feeds into
            outputs: [{ id: "out", name: "used by", kind: "data" }],
            meta: {
                // weak — the deref is transient; the node never retains
                resource: { kind: entry.kind, type: entry.type, deref: () => entry.deref() },
                uuid: entry.uuid,
                vram: mem.vram,
                cpu: mem.cpu,
                value: entry.type,
                // Format / Size / CPU / GPU rendered as their own rows by the card.
                ...(format ? { format } : {}),
                ...(size ? { size } : {}),
                ...(mips ? { mips } : {}),
                ...(mem.vram > 0 ? { memGpu: bytesLabel(mem.vram) } : {}),
                ...(mem.cpu > 0 ? { memCpu: bytesLabel(mem.cpu) } : {}),
                ...(dangling ? { dangling: true } : {}),
                ...(entry.alive ? {} : { collected: true }),
                ...(preview ? { mediaValue: true, thumbnail: preview.produce, thumbnailAspect: preview.aspect ?? 1 } : {}),
            },
        };
        nodeIds.set(key, node.id);
        nodeById.set(node.id, node);
        graph.nodes.push(node);
        return node.id;
    };

    // A consumer snapshot that is NOT itself a registry entry (a mesh, an
    // object, a scene). No memory; a weak `resource` handle (when the tracker
    // recorded one) so clicking selects the object's hierarchy row, same as a
    // registry node — see selectGraphNode. Its "used by" output feeds whatever
    // contains it (a mesh → its model); its inputs (added lazily) are the
    // resources it uses.
    const consumerNode = (consumer: ResourceUsage): string => {
        const key = `c:${consumer.uuid}`;
        const existing = nodeIds.get(key);
        if (existing) return existing;
        const ref = consumer.ref;
        const node: GraphNode = {
            id: key,
            typeRef: `resource/consumer`,
            title: consumer.name || consumer.type || "object",
            category: consumerCategory(consumer.type),
            inputs: [],
            outputs: [{ id: "out", name: "used by", kind: "data" }],
            meta: {
                consumerUuid: consumer.uuid,
                value: consumer.type || "",
                ...(ref ? { resource: { kind: "object", type: consumer.type, deref: () => ref.deref() } } : {}),
            },
        };
        nodeIds.set(key, node.id);
        nodeById.set(node.id, node);
        graph.nodes.push(node);
        return node.id;
    };

    // #region BFS (root-first order — redaction keeps the nearest prefix)

    const edgeKeys = new Set<string>();
    /** Wire a `resource` into its `consumer`, drawn producer → consumer
     *  (resource.out → consumer.in) so the resource sits on the LEFT — like an
     *  input in the shader graph. `slots` (the consumer's binding names for the
     *  resource) become named INPUT sockets on the CONSUMER — one edge per slot —
     *  so a material reads "map"/"normalMap". No slots → a fallback input.
     *  Call order stays (consumer, resource); only the emitted edge is reversed. */
    const addEdge = (consumer: string, resource: string, slots?: readonly string[], fallbackName = "uses"): void => {
        // Tolerate a non-array `slots`. A bare string has `.length` but no `.map`,
        // so it used to throw here and take the WHOLE graph panel down with it —
        // and slots arrive from recorded usage snapshots (host-derived), not just
        // from call sites we control.
        const list = Array.isArray(slots) ? slots : (typeof slots === "string" ? [slots] : undefined);
        const targets: Array<{ socket: string; name: string }> = list?.length
            ? list.map((s) => ({ socket: `slot:${s}`, name: s }))
            : [{ socket: "in", name: fallbackName }];
        const owner = nodeById.get(consumer); // the consumer carries the input socket
        for (const { socket, name } of targets) {
            const k = `${resource}->${consumer}:${socket}`;
            if (edgeKeys.has(k)) continue;
            edgeKeys.add(k);
            if (owner && !owner.inputs.some((i) => i.id === socket)) {
                owner.inputs.push({ id: socket, name, kind: "data" });
            }
            graph.dataEdges.push({ from: { node: resource, socket: "out" }, to: { node: consumer, socket } });
        }
    };

    /** Binding slots by which `consumerUuid` references `resourceEntry`
     *  (getUsages is the reverse index: resource → its consumers + slots). */
    const slotsFor = (resourceEntry: IResourceEntry, consumerUuid: string | undefined): readonly string[] | undefined => {
        if (!consumerUuid) return undefined;
        return service.getUsages(resourceEntry).find((u) => u.uuid === consumerUuid)?.slots;
    };

    // Frontier items expand: an ENTRY (resource) or a bare OBJECT (a mesh/scene
    // consumer — not a registry entry, but it USES resources, so it's an
    // intermediate node, not a leaf: model → MESH → geometry/material).
    type Ref = { kind: "entry"; entry: IResourceEntry } | { kind: "object"; uuid: string };
    let truncated = false;
    const capReached = () => graph.nodes.length >= maxNodes;
    const rootNode = entryNode(root);
    const seen = new Set<string>([`e${root.id}`]);
    let frontier: Ref[] = [{ kind: "entry", entry: root }];

    // Where the root CAME FROM: a direct edge to the model that loaded it
    // (recorded at load time — trustworthy, not inferred from the usage chain).
    // Labelled with the resource's glTF JSON pointer ("/textures/0") when known.
    // Terminal — the model isn't expanded into its other contents. Absent for a
    // resource loaded outside any model, or a model root itself.
    const origin = root.kind === "model" ? undefined : service.getOrigin(root);
    if (origin) addEdge(entryNode(origin.model), rootNode, undefined, origin.pointer ?? "from");

    // An ANIMATION MIXER does have usage edges (mixer → the object it animates,
    // clip → mixer), but the generic BFS would render them as flat resource
    // links. For a mixer we can say much more: one node per ACTION, with its
    // weight/running state, drawn clip → mixer so the clips sit on the LEFT
    // feeding the mixer, matching the resource convention. Clearing the frontier
    // below hands the mixer entirely to this richer view — the BFS never sees it,
    // so the two never draw the same edge twice.
    const animation = options.animationFor && root.alive
        ? options.animationFor(root.deref() ?? {})
        : undefined;
    if (animation) {
        applyMixerMeta(nodeById.get(rootNode)!, animation);
        animation.actions.forEach((action, index) => {
            if (graph.nodes.length >= maxNodes) { truncated = true; return; }
            const node: GraphNode = {
                id: `a${index}`,
                typeRef: "resource/action",
                title: action.clipName || `clip ${index}`,
                category: "animation",
                inputs: [],
                outputs: [{ id: "out", name: "plays on", kind: "data" }],
                meta: actionMeta(action),
            };
            nodeById.set(node.id, node);
            graph.nodes.push(node);
            // running actions name their socket by weight so the wiring itself
            // reads as the blend; idle ones stay quiet
            addEdge(rootNode, node.id, undefined, action.running ? weightLabel(action.weight) : "idle");
        });
        // nothing to walk — fall through to the shared tail (rollup/redaction)
        frontier = [];
    }

    const pushObject = (next: Ref[], consumer: ResourceUsage): string => {
        const id = consumerNode(consumer);
        const key = `c:${consumer.uuid}`;
        if (consumer.uuid && !seen.has(key)) { seen.add(key); next.push({ kind: "object", uuid: consumer.uuid }); }
        return id;
    };

    for (let hop = 0; hop < hops && frontier.length && !truncated; hop++) {
        const next: Ref[] = [];
        for (const ref of frontier) {
            if (truncated) break;

            if (ref.kind === "object") {
                // The resources this mesh/object uses (geometry, materials) — each
                // drawn resource → mesh, so they sit to the mesh's LEFT.
                const objId = nodeIds.get(`c:${ref.uuid}`)!;
                for (const used of usesIndex.get(ref.uuid) ?? []) {
                    if (capReached()) { truncated = true; break; }
                    addEdge(objId, entryNode(used), slotsFor(used, ref.uuid));
                    if (!seen.has(`e${used.id}`)) { seen.add(`e${used.id}`); next.push({ kind: "entry", entry: used }); }
                }
                continue;
            }

            const entry = ref.entry;
            const entryId = nodeIds.get(`e${entry.id}`)!;

            // A MODEL is the assembly SINK: its contents (meshes) flow INTO it
            // (mesh → model), so the model sits on the right, its meshes to the
            // left, each expanding further left to its resources.
            if (entry.kind === "model") {
                for (const obj of service.getUsages(entry)) {
                    if (capReached()) { truncated = true; break; }
                    addEdge(entryId, pushObject(next, obj), undefined, "contains");
                }
                // Clips are NOT in the object graph — they hang off `gltf.animations`,
                // so the usage sweep never links them and a model with four clips drew
                // no animation edge at all. They carry an ORIGIN instead (recorded at
                // registration), which is the only link back to their file.
                for (const clip of service.entries("clip")) {
                    if (capReached()) { truncated = true; break; }
                    const origin = service.getOrigin(clip);
                    if (origin?.model.id !== entry.id) continue;
                    addEdge(entryId, entryNode(clip), undefined, "contains");
                }
                continue;
            }

            // Resources this entry uses (entry ← E), each drawn E → entry so the
            // used resource sits to the LEFT, its binding slot labelling the edge.
            for (const used of usesIndex.get(resourceUsageUuid(entry)) ?? []) {
                if (capReached()) { truncated = true; break; }
                addEdge(entryId, entryNode(used), slotsFor(used, resourceUsageUuid(entry)));
                if (!seen.has(`e${used.id}`)) { seen.add(`e${used.id}`); next.push({ kind: "entry", entry: used }); }
            }
            if (truncated) break;
            // Consumers of this entry (they sit to the RIGHT: entry → consumer) —
            // a registry consumer recurses; a bare object (mesh) expands.
            for (const consumer of service.getUsages(entry)) {
                if (capReached()) { truncated = true; break; }
                const consumerEntry = consumer.uuid ? byUuid.get(consumer.uuid) : undefined;
                if (consumerEntry) {
                    addEdge(entryNode(consumerEntry), entryId, consumer.slots);
                    if (!seen.has(`e${consumerEntry.id}`)) { seen.add(`e${consumerEntry.id}`); next.push({ kind: "entry", entry: consumerEntry }); }
                } else {
                    addEdge(pushObject(next, consumer), entryId, consumer.slots);
                }
            }
        }
        frontier = next;
    }

    if (truncated) {
        // a source-side badge ("there are more resources"), so it feeds the root
        graph.nodes.push({
            id: "more", typeRef: "resource/more", title: "… more resources", category: "more",
            inputs: [], outputs: [{ id: "out", name: "", kind: "data" }],
            meta: { truncated: true },
        });
        addEdge(rootNode, "more");
    }

    // #region memory rollup (root's reachable upstream footprint, dedup shared)

    const rollup = reachableMemory(graph, rootNode, options.dedupedMemoryFor);
    const rootMeta = graph.nodes[0].meta!;
    rootMeta.memoryRollup = rollup.vram; // GPU total (number, for AI/back-compat)
    rootMeta.memoryRollupCpu = rollup.cpu; // CPU total (number, for AI)
    // GPU/CPU dependency totals rendered as separate accent rows by the card.
    if (rollup.count > 0) {
        if (rollup.vram > 0) rootMeta.rollupGpu = bytesLabel(rollup.vram);
        if (rollup.cpu > 0) rootMeta.rollupCpu = bytesLabel(rollup.cpu);
        rootMeta.rollupCount = rollup.count;
    }

    if (options.redactAfter != null) redactResourceGraph(graph, options.redactAfter);
    return graph;
}

/** GPU + CPU memory of everything the root DEPENDS ON — its upstream producers
 *  (edges point resource → consumer, so a root's dependencies are its in-edges).
 *  `count` = resources with any footprint. When `dedupedMemoryFor` is given, the
 *  totals dedupe storage SHARED between resources (see helper.memory); otherwise
 *  they're a per-node sum (nodes are already deduped, buffers are not). */
function reachableMemory(
    graph: NodeGraph,
    rootNode: string,
    dedupedMemoryFor?: (resources: object[]) => ResourceMemory,
): { vram: number; cpu: number; count: number } {
    const upstream = new Map<string, string[]>();
    for (const e of graph.dataEdges) {
        let l = upstream.get(e.to.node);
        if (!l) upstream.set(e.to.node, l = []);
        l.push(e.from.node);
    }
    const nodeById = new Map(graph.nodes.map((n) => [n.id, n] as const));
    const visited = new Set<string>([rootNode]);
    const stack = [rootNode];
    let vram = 0, cpu = 0, count = 0;
    const liveResources: object[] = [];
    while (stack.length) {
        for (const to of upstream.get(stack.pop()!) ?? []) {
            if (visited.has(to)) continue;
            visited.add(to);
            stack.push(to);
            const meta = nodeById.get(to)?.meta;
            const v = typeof meta?.vram === "number" ? meta.vram : 0;
            const c = typeof meta?.cpu === "number" ? meta.cpu : 0;
            if (v > 0 || c > 0) {
                vram += v; cpu += c; count++;
                // collect the live object so the total can dedupe shared buffers
                const live = (meta?.resource as { deref?: () => unknown } | undefined)?.deref?.();
                if (live) liveResources.push(live as object);
            }
        }
    }
    // deduped total when the estimator is injected AND every counted resource was
    // still alive to deref (else a dead one would silently drop from the total —
    // fall back to the per-node sum in that case)
    if (dedupedMemoryFor && liveResources.length === count) {
        const deduped = dedupedMemoryFor(liveResources);
        return { vram: deduped.vram, cpu: deduped.cpu, count };
    }
    return { vram, cpu, count };
}

/**
 * Pro gate: keep the first `limit` nodes (root-first BFS order) fully
 * detailed; strip the identity/memory of the rest and mark `meta.redacted`
 * (they still render — structure/edges intact — as locked teases). The
 * memory ROLLUP on the root is deliberately left visible: seeing "52 MB
 * across 12 resources" with only 3 shown is the upgrade motivator.
 */
export function redactResourceGraph(graph: NodeGraph, limit: number): void {
    graph.nodes.forEach((node, index) => {
        if (index < limit || node.meta?.truncated) return;
        node.title = "Locked";
        node.meta = {
            redacted: true,
            // keep the sizing category so the graph doesn't reflow when unlocked
            ...(typeof node.meta?.vram === "number" ? { vram: 0 } : {}),
            ...(typeof node.meta?.cpu === "number" ? { cpu: 0 } : {}),
        };
        // no resource/consumer handle → cannot inspect or reveal while locked
    });
}

/** Guess a display category for a bare consumer snapshot from its three type. */
function consumerCategory(type: string | undefined): string {
    if (!type) return "consumer";
    if (type === "Scene") return "scene";
    if (/Camera$/.test(type)) return "camera";
    if (/Light$/.test(type)) return "light";
    return "mesh";
}
